<template>
  <v-container
    class="details-bg"
    fluid
    :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'"
  >
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="details-card-modern" flat rounded="xl">
          <div
            class="details-header-modern"
            :class="{
              'rtl-header': $i18n.locale === 'he',
              'ltr-header': $i18n.locale !== 'he',
            }"
          >
            <AlertBackButton />
            <span class="details-title-modern">{{ $t("alertDetails") }}</span>
          </div>

          <!-- Debug info -->
          <div
            v-if="!alert"
            class="debug-info pa-4"
            :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'"
          >
            <v-alert type="warning" variant="tonal" dense>
              <div class="alert-message">
                <strong>{{ $t("alertNotFound") }}:</strong>
                {{ extractNumericId($route.params.identifier) }}
              </div>
              <div v-text="alertPassedText" class="alert-message mt-2" />
            </v-alert>
          </div>

          <v-row v-else class="details-content-row" align="stretch" no-gutters>
            <div class="details-flex-row">
              <div class="xml-col-modern">
                <v-card class="xml-card-modern" flat>
                  <div style="max-height: 340px; overflow: auto">
                    <AlertXmlViewer :xmlContent="alert?.rawXml || ''" />
                  </div>
                </v-card>
              </div>
              <div class="meta-col-modern">
                <AlertMeta :alert="alert" />
              </div>
            </div>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import AlertMeta from "./AlertMeta.vue";
import AlertXmlViewer from "./AlertXmlViewer.vue";
import AlertResponses from "./AlertResponses.vue";
import AlertBackButton from "../ui/AlertBackButton.vue";

export default {
  props: {
    dir: {
      type: String,
      default: "ltr",
    },
  },
  components: {
    AlertMeta,
    AlertXmlViewer,
    AlertResponses,
    AlertBackButton,
  },
  computed: {
    ...mapGetters(["alerts", "uiTtlMinutes"]),
    alert() {
      const id = this.$route.params.identifier;
      const foundAlert = this.alerts.find(
        (a) => String(a.identifier) === String(id)
      );
      return foundAlert;
    },
    info() {
      return this.alert && Array.isArray(this.alert.info)
        ? this.alert.info[0]
        : this.alert?.info;
    },
    alertPassedText() {
      return this.$t("alertPassedTtl").replace("{minutes}", this.uiTtlMinutes);
    },
  },
  methods: {
    extractNumericId(id) {
      // Extract only numeric characters from the ID
      const numericPart = id.toString().replace(/\D/g, "");
      return numericPart || "0";
    },
  },
  mounted() {
    // Component mounted
  },
};
</script>

<style scoped>
.details-bg {
  background: var(--v-theme-background);
  min-height: 100vh;
  padding-top: 32px;
  padding-bottom: 32px;
}
.details-card-modern {
  background: var(--v-theme-surface);
  border-radius: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 32px 28px 28px 28px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.details-header-modern {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 24px;
  position: relative;
  gap: 16px;
}
.rtl-header {
  flex-direction: row-reverse;
  text-align: right;
}
.ltr-header {
  flex-direction: row;
  text-align: left;
}
.back-btn-modern {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: var(--v-theme-surface);
  border-radius: 50%;
  box-shadow: none;
}
.details-title-modern {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--v-theme-primary);
  letter-spacing: 1px;
}
.details-content-row {
  margin-top: 0;
  margin-bottom: 0;
}
.meta-col-modern {
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  height: 70vh;
}
.xml-col-modern {
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  min-width: 300px;
}
.extra-info-modern {
  margin-top: 18px;
  background: none;
  border-radius: 8px;
  padding: 0;
  box-shadow: none;
}
.extra-label-modern {
  color: var(--v-theme-secondary);
  font-size: 0.97rem;
  font-weight: 500;
  margin-top: 8px;
}
.extra-value-modern {
  font-weight: bold;
  margin-bottom: 8px;
  color: var(--v-theme-primary);
}
.status-chip-modern {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: none;
}
.debug-info {
  background: var(--v-theme-surface-variant);
  border-radius: 8px;
  margin-bottom: 16px;
}
.xml-card-modern {
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 0;
  height: 70vh;
  overflow: hidden;
  background: var(--v-theme-surface);
  display: flex;
  flex-direction: column;
}
.details-flex-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  gap: 8px;
}
.xml-col-modern {
  flex: 2 1 0;
  min-width: 0;
}
.meta-col-modern {
  flex: 1 1 0;
  min-width: 0;
  max-width: 220px;
}
@media (max-width: 960px) {
  .details-card-modern {
    padding: 18px 6px 16px 6px;
  }
  .xml-card-modern {
    height: auto;
    min-height: 220px;
  }
}
@media (max-width: 768px) {
  .details-bg,
  .v-container,
  .v-row,
  .v-col {
    margin: 0 !important;
    padding: 0 !important;
  }
  .details-card-modern {
    padding: 1em 0.5em;
  }
  .details-header-modern {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.7em;
    padding: 0.7em 0.2em;
    font-size: 1.15em;
  }
  .details-title-modern {
    font-size: 1.3em;
  }
  .xml-col-modern,
  .meta-col-modern {
    padding: 0.7em 0.2em !important;
    margin: 0 !important;
    display: block !important;
  }
  .xml-card-modern {
    max-height: 40vh !important;
    overflow-y: auto !important;
  }
  .meta-col-modern {
    margin: 0 auto !important;
    float: none !important;
    font-size: 1em !important;
    line-height: 1.1 !important;
    word-break: break-word !important;
    white-space: normal !important;
    min-height: unset !important;
    max-height: 180px !important;
    overflow-y: auto !important;
  }
  .details-flex-row {
    flex-direction: row;
  }
  .xml-col-modern {
    flex: 3 1 0;
    width: 50% !important;
    max-width: 50% !important;
    max-height: 100% !important;
  }
  .meta-col-modern {
    flex: 1 1 0;
    width: 50% !important;
    max-width: 50% !important;
    max-height: 100% !important;
    min-width: 0 !important;
  }
}
</style>
